
import Vue, { PropType } from 'vue';
import {
  Assessment,
  AssessmentResult,
  ASSESSMENT_STATUS,
  ASSESSMENT_TYPE,
} from '@/models/greathall/assessment.model';
import PostGradTCCAssessmentModal from './PostGradTCCAssessmentModal.vue';
import PostGradAssessmentResultModal from './PostGradAssessmentResultModal.vue';
import PostGradAssessmentUnavailableModal from './PostGradAssessmentUnavailableModal.vue';
import PostGradModuleOneAssessmentAvailableModal from './PostGradModuleOneAssessmentAvailableModal.vue';
import PostGradModuleTwoAssessmentAvailableModal from './PostGradModuleTwoAssessmentAvailableModal.vue';
import PostGradModuleThreeAssessmentAvailableModal from './PostGradModuleThreeAssessmentAvailableModal.vue';
import dayjs from 'dayjs';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { ContractModel } from '@descomplica/dex-models';
import { WarningLevels, DocumentsStatus } from '@/enum/postgradStudentDocuments.enum';
import moment from 'moment';
import { StudentDocumentsStatusInfo } from '@/helpers/postgradStudentDocuments/interfaces';

export default Vue.extend({
  components: {
    PostGradTCCAssessmentModal,
    PostGradAssessmentResultModal,
    PostGradAssessmentUnavailableModal,
    PostGradModuleOneAssessmentAvailableModal,
    PostGradModuleTwoAssessmentAvailableModal,
    PostGradModuleThreeAssessmentAvailableModal,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    assessment: {
      type: Object as PropType<Assessment>,
      required: true,
    },
    isFirstAssessment: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isActive: this.active,
      isLoading: false,
    };
  },
  computed: {
    currentCourseId(): number {
      return this.$store.getters.currentCourse.id;
    },
    assessmentDetails(): Assessment {
      return this.$store.getters.assessmentDetails;
    },
    type(): ASSESSMENT_TYPE {
      return this.assessment.assessmentType;
    },
    status(): ASSESSMENT_STATUS {
      return this.assessment.assessmentStatusByUser;
    },
    lastAssessmentResult(): AssessmentResult | null {
      return this.assessment.assessmentResultsByUser[0] || null;
    },
    isRetryAvailable(): boolean {
      const { gradingDate } = this.assessment.assessmentResultsByUser[0] || {};
      const dateAvailable = dayjs(gradingDate).add(this.assessment.daysToRetry, 'days');

      return dateAvailable.unix() < dayjs().unix();
    },
    showResultModal(): boolean {
      return (
        (this.status === ASSESSMENT_STATUS.PASSED ||
          (this.status === ASSESSMENT_STATUS.RETRY && !this.isRetryAvailable)) &&
        (this.type === ASSESSMENT_TYPE.PRESENTATION || this.type === ASSESSMENT_TYPE.CASE_STUDY)
      );
    },
    showUnavailableModal(): boolean {
      return (
        this.status === ASSESSMENT_STATUS.UNAVAILABLE ||
        this.areAssessmentsBlockedByPendingUserDocuments
      );
    },
    showModuleOneAvailableModal(): boolean {
      return (
        this.type === ASSESSMENT_TYPE.DEX &&
        (this.status === ASSESSMENT_STATUS.AVAILABLE ||
          this.status === ASSESSMENT_STATUS.WAITING_GRADING ||
          (this.status === ASSESSMENT_STATUS.RETRY && this.isRetryAvailable))
      );
    },
    showModuleTwoAvailableModal(): boolean {
      return (
        this.type === ASSESSMENT_TYPE.CASE_STUDY &&
        (this.status === ASSESSMENT_STATUS.AVAILABLE ||
          (this.status === ASSESSMENT_STATUS.RETRY && this.isRetryAvailable))
      );
    },
    showModuleThreeAvailableModal(): boolean {
      return (
        this.type === ASSESSMENT_TYPE.PRESENTATION &&
        (this.status === ASSESSMENT_STATUS.AVAILABLE ||
          (this.status === ASSESSMENT_STATUS.RETRY && this.isRetryAvailable))
      );
    },
    showTCCModal(): boolean {
      return this.type === ASSESSMENT_TYPE.TCC && this.status !== ASSESSMENT_STATUS.UNAVAILABLE;
    },
    userDocumentsStatus(): StudentDocumentsStatusInfo | null {
      return this.$store.getters.studentDocumentsStatusByCourseId(this.currentCourseId);
    },
    areAssessmentsBlockedByPendingUserDocuments(): boolean {
      if (!this.userDocumentsStatus) {
        return false;
      }

      return this.userDocumentsStatus.delayWarningLevel === WarningLevels.FINAL;
    },
    daysAfterContractStart(): number {
      return moment().diff(this.courseContract.createdAt, 'days');
    },
    courseContract(): ContractModel {
      return this.$store.getters.contractByCourseId(this.currentCourseId);
    },
  },
  watch: {
    active(value) {
      this.isActive = value;
    },
  },
  async created() {
    const data = {
      dexCourseId: this.currentCourseId,
      assessmentId: this.assessment.id,
    };

    this.isLoading = true;
    if (this.showUnavailableModal) {
      const payload: any = {
        relatedAssessmentCategory: this.assessment.category?.name || '',
        days_after_contract_start: this.daysAfterContractStart,
        warning_level: null,
        status: null,
      };

      if (this.userDocumentsStatus) {
        payload.warning_level =
          this.userDocumentsStatus.delayWarningLevel &&
          WarningLevels[this.userDocumentsStatus.delayWarningLevel];

        payload.status = DocumentsStatus[this.userDocumentsStatus.status];
      }

      Analytics.trackEvent(EVENT_LABELS.unavailableAssessmentsModalView, payload);

      return;
    }
    if (this.showModuleOneAvailableModal) {
      await this.$store.dispatch('PULL_ASSESSMENT_MODULE_ONE_DATA', data);
      Analytics.trackEvent(EVENT_LABELS.dexAssessmentModalView, {
        relatedAssessmentCategory: this.assessment.category?.name || '',
      });
    }
    if (this.showModuleTwoAvailableModal) {
      await this.$store.dispatch('PULL_ASSESSMENT_MODULE_TWO_DATA', data);
      Analytics.trackEvent(EVENT_LABELS.studyCaseModalView, {
        relatedAssessmentCategory: this.assessment.category?.name || '',
      });
    }
    if (this.showModuleThreeAvailableModal) {
      Analytics.trackEvent(EVENT_LABELS.pitchModalView, {
        relatedAssessmentCategory: this.assessment.category?.name || '',
      });
    }
    if (this.showTCCModal) {
      await this.$store.dispatch('PULL_ASSESSMENT_TCC_DATA', data);
      Analytics.trackEvent(EVENT_LABELS.tccModalView, {
        relatedAssessmentCategory: this.assessment.category?.name || '',
      });
    }
    if (this.showResultModal) {
      await this.$store.dispatch('PULL_ASSESSMENT_RESULTS_DATA', data);
      Analytics.trackEvent(EVENT_LABELS.assessmentResultView, {
        relatedAssessmentCategory: this.assessment.category?.name || '',
        assessmentType: this.assessment.assessmentType,
      });
    }
    this.isLoading = false;
  },
  methods: {
    updateActive(active: boolean) {
      this.$emit('update:active', active);
    },
  },
});
